import React, { useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

import IconButton from "../../../../../packages/IconButton";
import WPHeading from "../../../../../packages/WPHeading";

import {
  getDropdownPosition,
  shouldCloseDropdown,
} from "./LicenseDropdownActionMenu.utils";

import { KEYBOARD_KEYS } from "../../../../../constants/global.constants";

import { combineClassNames } from "../../../../../utils/appUtils";

import "./LicenseDropdownActionMenu.scss";

const LicenseDropdownActionMenu = ({
  menuOptions,
  buttonRef,
  isActionMenuVisible,
  setActionMenuVisible,
  onOptionClick,
}) => {
  const dropdownMenuRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);

  /**
   * Calculates and updates the dropdown menu position based on button and menu refs.
   */
  const updateDropdownPosition = () => {
    const position = getDropdownPosition({
      buttonRef,
      menuRef: dropdownMenuRef,
    });
    if (position) setDropdownPosition(position);
  };

  /**
   * Checks if the dropdown menu has left the visible table area and closes it if necessary.
   */
  const checkDropdownBoundary = () => {
    shouldCloseDropdown({
      menuRef: dropdownMenuRef,
      buttonRef,
      setActionMenuVisible,
    });
  };

  /**
   * Closes the dropdown menu when a click occurs outside the dropdown or its trigger button.
   */
  const handleOutsideClick = (event) => {
    const dropdownElement = dropdownMenuRef.current;
    const buttonElement = buttonRef.current;
    if (
      dropdownElement &&
      !dropdownElement.contains(event.target) &&
      buttonElement &&
      !buttonElement.contains(event.target)
    ) {
      setActionMenuVisible(false);
    }
  };

  /**
   * Handles dropdown positioning, closing on scroll/resize,
   * and outside click events while visible.
   */
  useLayoutEffect(() => {
    if (!isActionMenuVisible) return;
    updateDropdownPosition();
    const handleScrollOrResize = () => {
      updateDropdownPosition();
      checkDropdownBoundary();
    };
    document.addEventListener("mousedown", handleOutsideClick);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", updateDropdownPosition);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [isActionMenuVisible, buttonRef]);

  const handleOptionClick = (option) => onOptionClick(option);

  const handleOptionKeyDown = (event, option) => {
    if (event.key === KEYBOARD_KEYS.ENTER) {
      onOptionClick(option);
    }
  };

  if (!isActionMenuVisible || !dropdownPosition) return null;

  return ReactDOM.createPortal(
    <div
      ref={dropdownMenuRef}
      className="license-action-menu-dropdown"
      style={{
        "--dropdown-top": `${dropdownPosition.top}px`,
        "--dropdown-left": `${dropdownPosition.left}px`,
      }}
    >
      {menuOptions.map((menuOption) => {
        const isDisabled = !!menuOption.disabled;
        return (
          <div
            key={menuOption.id}
            id={`license-menu-${menuOption.id}`}
            className={combineClassNames(
              "license-action-menu-item",
              isDisabled && "disabled",
              menuOption.isBlockUser && "is-block-user",
            )}
            onClick={
              (!isDisabled && (() => handleOptionClick(menuOption))) ||
              undefined
            }
            onKeyDown={
              (!isDisabled &&
                ((event) => handleOptionKeyDown(event, menuOption))) ||
              undefined
            }
            tabIndex={0}
            aria-disabled={isDisabled}
          >
            <span className="license-action-menu-item-content">
              <IconButton className="user-svg" disabled={isDisabled}>
                {menuOption.icon}
              </IconButton>
              <WPHeading
                text={menuOption.label}
                fontNoto
                type="default"
                size="small"
                className={combineClassNames(
                  "license-action-menu-item-label",
                  isDisabled && "disabled",
                  menuOption.isBlockUser && "block-user-label",
                )}
                secondary
              />
            </span>
          </div>
        );
      })}
    </div>,
    document.body,
  );
};

export default LicenseDropdownActionMenu;
